import moment from "moment";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { dashboardApi, DashboardData, DashboardStats } from "./dashboard.api";

interface DashboardState {
  data: DashboardData | null;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

const initialState: DashboardState = {
  data: null,
  loading: false,
  error: null,
  lastUpdated: null,
};

export const fetchDashboardData = createAsyncThunk("dashboard/fetchData", async (_, { rejectWithValue }) => {
  try {
    return await dashboardApi.getDashboardData();
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Failed to fetch dashboard data");
  }
});

export const refreshDashboardStats = createAsyncThunk("dashboard/refreshStats", async (_, { rejectWithValue }) => {
  try {
    return await dashboardApi.getDashboardStats();
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Failed to refresh stats");
  }
});

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearData: (state) => {
      state.data = null;
      state.lastUpdated = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch dashboard data
      .addCase(fetchDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.lastUpdated = moment().toISOString();
        state.error = null;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Refresh stats
      .addCase(refreshDashboardStats.fulfilled, (state, action) => {
        if (state.data) {
          state.data.stats = action.payload;
          state.lastUpdated = moment().toISOString();
        }
      });
  },
});

export const { clearError, clearData } = dashboardSlice.actions;
export default dashboardSlice.reducer;
